import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, Briefcase, Calendar, LogOut, TrendingUp, Download, CheckCircle, XCircle, Clock, AlertCircle, Phone, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import api, { API_BASE } from '../lib/api';

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  converted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('contacts');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [careers, setCareers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('admin_user');
    if (!stored) {
      navigate('/admin/login');
      return;
    }
    setUser(JSON.parse(stored));
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, n, cr, b] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/contacts'),
        api.get('/admin/newsletter'),
        api.get('/admin/career'),
        api.get('/admin/bookings'),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setNewsletter(n.data);
      setCareers(cr.data);
      setBookings(b.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status, notes: '' });
      toast({ title: 'Status Updated', description: `Marked as ${status}` });
      loadAll();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast({ title: 'Deleted' });
      loadAll();
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const downloadResume = async (appId, filename) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/career/${appId}/resume`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'resume';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download Failed', variant: 'destructive' });
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="border-none shadow-lg hover:shadow-xl transition-all">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
            <div className="text-3xl font-bold text-gray-900">{value}</div>
          </div>
          <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center shadow-lg`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-bold text-gray-900">FounderScale Admin</div>
                <div className="text-xs text-gray-500">Welcome, {user?.name || 'Admin'}</div>
              </div>
            </div>
            <Button onClick={logout} variant="outline" className="border-gray-300" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Leads" value={stats.contacts.total} color="bg-gradient-to-br from-blue-500 to-indigo-600" />
            <StatCard icon={AlertCircle} label="New Leads" value={stats.contacts.new} color="bg-gradient-to-br from-amber-500 to-orange-600" />
            <StatCard icon={CheckCircle} label="Converted" value={stats.contacts.converted} color="bg-gradient-to-br from-emerald-500 to-green-600" />
            <StatCard icon={Calendar} label="Upcoming Calls" value={stats.bookings.upcoming} color="bg-gradient-to-br from-purple-500 to-violet-600" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
          {[
            { id: 'contacts', label: `Contacts (${contacts.length})`, icon: Users },
            { id: 'bookings', label: `Bookings (${bookings.length})`, icon: Calendar },
            { id: 'newsletter', label: `Newsletter (${newsletter.length})`, icon: Mail },
            { id: 'careers', label: `Careers (${careers.length})`, icon: Briefcase }
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-4 py-3 font-semibold text-sm transition-all flex items-center gap-2 border-b-2 -mb-px ${tab === t.id ? 'text-emerald-600 border-emerald-600' : 'text-gray-600 border-transparent hover:text-emerald-600'}`}>
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading && <div className="text-center py-12 text-gray-500">Loading...</div>}

        {/* Contacts Tab */}
        {tab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="text-center py-16 text-gray-500">No contacts yet</div>
            ) : (
              contacts.map(c => (
                <Card key={c._id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg text-gray-900">{c.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusColors[c.status] || statusColors.new}`}>{c.status}</span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-gray-400" />{c.startup_name}</div>
                          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" /><a href={`mailto:${c.email}`} className="text-emerald-600 hover:underline">{c.email}</a></div>
                          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" /><a href={`tel:${c.phone}`} className="text-emerald-600 hover:underline">{c.phone}</a></div>
                        </div>
                        <p className="mt-3 text-gray-700 bg-gray-50 p-3 rounded-lg text-sm">{c.message}</p>
                        <div className="text-xs text-gray-400 mt-2">{new Date(c.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        <Button onClick={() => updateContactStatus(c._id, 'contacted')} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`contact-${c._id}-contacted`}>
                          <Clock className="h-3 w-3 mr-1" />Mark Contacted
                        </Button>
                        <Button onClick={() => updateContactStatus(c._id, 'converted')} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`contact-${c._id}-converted`}>
                          <CheckCircle className="h-3 w-3 mr-1" />Mark Converted
                        </Button>
                        <Button onClick={() => updateContactStatus(c._id, 'rejected')} variant="outline" size="sm" className="border-rose-300 text-rose-700 hover:bg-rose-50">
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                        <Button onClick={() => deleteContact(c._id)} variant="outline" size="sm" className="border-gray-300 text-gray-600">
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {tab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <div className="text-center py-16 text-gray-500">No bookings yet</div>
            ) : (
              bookings.map(b => (
                <Card key={b._id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-5 w-5 text-emerald-600" />
                          <span className="font-bold text-gray-900">{b.date} at {b.time} UTC</span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="font-semibold text-gray-900">{b.name} {b.startup_name && `(${b.startup_name})`}</div>
                          <div><Mail className="h-3 w-3 inline mr-1" />{b.email}</div>
                          <div><Phone className="h-3 w-3 inline mr-1" />{b.phone}</div>
                          {b.notes && <div className="bg-gray-50 p-2 rounded mt-2">{b.notes}</div>}
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">{b.status}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Newsletter Tab */}
        {tab === 'newsletter' && (
          <Card>
            <CardContent className="p-0">
              {newsletter.length === 0 ? (
                <div className="text-center py-16 text-gray-500">No subscribers yet</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Email</th>
                      <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Name</th>
                      <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsletter.map(n => (
                      <tr key={n._id} className="border-t hover:bg-gray-50">
                        <td className="p-4 text-sm font-medium text-gray-900">{n.email}</td>
                        <td className="p-4 text-sm text-gray-600">{n.name || '-'}</td>
                        <td className="p-4 text-sm text-gray-500">{new Date(n.subscribed_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Careers Tab */}
        {tab === 'careers' && (
          <div className="space-y-4">
            {careers.length === 0 ? (
              <div className="text-center py-16 text-gray-500">No career applications yet</div>
            ) : (
              careers.map(c => (
                <Card key={c._id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900">{c.name}</h3>
                        <div className="text-sm text-emerald-600 font-semibold mb-2">Applying for: {c.position}</div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div><Mail className="h-3 w-3 inline mr-1" />{c.email}</div>
                          <div><Phone className="h-3 w-3 inline mr-1" />{c.phone}</div>
                          {c.cover_letter && <div className="bg-gray-50 p-3 rounded-lg mt-2">{c.cover_letter}</div>}
                        </div>
                      </div>
                      <Button onClick={() => downloadResume(c._id, c.resume_filename)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Download className="h-4 w-4 mr-2" />Resume
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
